// E8RightTrapezoidAreaSideClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 12) - pole trapezu prostokątnego i najdluzszy bok.

import React from "react";
import RightTrapezoidDiagram from "../../../../components/diagrams/RightTrapezoidDiagram";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const CASES = [
  { baseA: 9, baseB: 1, h: 6, hyp: 10, area: 30 },
  { baseA: 8, baseB: 2, h: 8, hyp: 10, area: 40 },
  { baseA: 8, baseB: 3, h: 12, hyp: 13, area: 66 },
  { baseA: 4, baseB: 1, h: 4, hyp: 5, area: 10 },
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData(values) {
  return { ...values, areaDistractor: 2 * values.area };
}

function createOriginalTask() {
  return {
    type: "E8RightTrapezoidAreaSideClosedTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 12 },
    tags: ["e8", "planimetria", "trapez", "pole", "pitagoras"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8RightTrapezoidAreaSideClosedTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_12" },
    tags: ["e8", "planimetria", "trapez", "pole", "pitagoras"],
  };
}

function Diagram({ data }) {
  const { baseA, baseB, h } = data;
  return (
    <div style={{ display: "grid", placeItems: "center", margin: "18px 0 30px" }}>
      <div style={{ width: "min(100%, 420px)" }}>
        <RightTrapezoidDiagram
          a={baseB}
          b={baseA}
          h={h}
          showValues={false}
          topLabel={`${baseB}\\text{ cm}`}
          bottomLabel={`${baseA}\\text{ cm}`}
          heightLabel={`${h}\\text{ cm}`}
          showRightAngles
          size={420}
          padTop={42}
          padBottom={54}
          padX={42}
        />
      </div>
    </div>
  );
}

export function renderTask({ data }) {
  const { area, areaDistractor, baseA, hyp } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Na rysunku przedstawiono trapez prostokątny oraz podano długości trzech jego boków.</p>
      <Diagram data={data} />
      <p>
        <strong>
          Uzupełnij zdania. Wybierz odpowiedź spośród oznaczonych literami A i B oraz
          odpowiedź spośród oznaczonych literami C i D.
        </strong>
      </p>
      <p>Pole tego trapezu jest równe:</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
        <ChoiceOptionMath letter="A" math={`${area}\\text{ cm}^2`} />
        <ChoiceOptionMath letter="B" math={`${areaDistractor}\\text{ cm}^2`} />
      </div>
      <p>Najdłuższy bok tego trapezu ma długość:</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <ChoiceOptionMath letter="C" math={`${baseA}\\text{ cm}`} />
        <ChoiceOptionMath letter="D" math={`${hyp}\\text{ cm}`} />
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  return (
    <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
      <div>
        <div style={{ marginBottom: 4 }}>Pole trapezu:</div>
        <ABCDInput
          options={[{ label: "A", textNode: "A" }, { label: "B", textNode: "B" }]}
          value={ua.ab}
          onChange={(v) => onChange?.({ ...ua, ab: v })}
          disabled={disabled}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Najdłuższy bok:</div>
        <ABCDInput
          options={[{ label: "C", textNode: "C" }, { label: "D", textNode: "D" }]}
          value={ua.cd}
          onChange={(v) => onChange?.({ ...ua, cd: v })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function checkAnswer(_task, userAnswer = {}) {
  return userAnswer?.ab === 0 && userAnswer?.cd === 1;
}

export function renderAnswer({ data }) {
  return (
    <span style={{ display: "inline-flex", gap: 12, flexWrap: "wrap" }}>
      <ChoiceOptionMath letter="A" math={`${data.area}\\text{ cm}^2`} />
      <ChoiceOptionMath letter="D" math={`${data.hyp}\\text{ cm}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  if (userAnswer?.ab == null && userAnswer?.cd == null) return <i>(brak odpowiedzi)</i>;
  const abLetter = userAnswer?.ab === 0 ? "A" : userAnswer?.ab === 1 ? "B" : "-";
  const cdLetter = userAnswer?.cd === 0 ? "C" : userAnswer?.cd === 1 ? "D" : "-";
  return <span>{abLetter}, {cdLetter}</span>;
}

export function renderExplanation({ data }) {
  const { baseA, baseB, h, area, hyp } = data;
  const diff = baseA - baseB;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Pole trapezu prostokątnego (podstawy:{" "}
        <InlineMath math={`${baseA}\\text{ cm}`} /> i{" "}
        <InlineMath math={`${baseB}\\text{ cm}`} />, wysokość:{" "}
        <InlineMath math={`${h}\\text{ cm}`} />):
      </p>
      <BlockMath
        math={`P = \\frac{(${baseA} + ${baseB}) \\cdot ${h}}{2} = \\frac{${baseA + baseB} \\cdot ${h}}{2} = ${area}\\text{ cm}^2`}
      />
      <p>
        Poprawna odpowiedź to <InlineMath math="\text{A}" />.
      </p>
      <p>
        <strong>Krok 2.</strong> Długość ukośnego boku z twierdzenia Pitagorasa (różnica
        podstaw: <InlineMath math={`${diff}\\text{ cm}`} />, wysokość:{" "}
        <InlineMath math={`${h}\\text{ cm}`} />):
      </p>
      <BlockMath
        math={`c = \\sqrt{${diff}^2 + ${h}^2} = \\sqrt{${diff * diff} + ${h * h}} = \\sqrt{${diff * diff + h * h}} = ${hyp}\\text{ cm}`}
      />
      <p>
        Boki trapezu: <InlineMath math={`${baseB}\\text{ cm}`} />,{" "}
        <InlineMath math={`${h}\\text{ cm}`} />, <InlineMath math={`${baseA}\\text{ cm}`} />,{" "}
        <InlineMath math={`${hyp}\\text{ cm}`} />. Najdłuższy to{" "}
        <InlineMath math={`${hyp}\\text{ cm}`} />.
      </p>
      <p>
        Poprawna odpowiedź to <InlineMath math="\text{D}" />.
      </p>
    </div>
  );
}
